import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material'
import {
  School as SchoolIcon,
  LocationOn,
  Public,
  Email,
  Phone,
  Person,
  Star,
  StarBorder,
} from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { DIVISION_LABELS, formatCityState } from '@tnf-recruit/shared'
import { useSchoolDetail } from '../hooks/useSchoolSearch'
import { useStudentBookmarks } from '../hooks/useBookmarks'

export const SchoolDetailPage = () => {
  const { id } = useParams()
  const { data, isLoading, error } = useSchoolDetail(id)
  const { bookmarks, addBookmark, removeBookmark } = useStudentBookmarks()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error ? 'Failed to load school.' : 'School not found.'}
      </Alert>
    )
  }

  const { school, recruiters } = data

  const existingBookmark = bookmarks.data?.find(b => b.schoolId === school.id)
  const isBookmarked = !!existingBookmark

  const handleToggleBookmark = () => {
    if (isBookmarked && existingBookmark) {
      removeBookmark.mutate(existingBookmark.id)
    } else {
      addBookmark.mutate({ schoolId: school.id })
    }
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* School Header */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <SchoolIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                </Grid>
                <Grid item xs>
                  <Typography variant="h4" gutterBottom>
                    {school.name}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip label={DIVISION_LABELS[school.division]} color="primary" />
                    {school.conference && <Chip label={school.conference} />}
                    <Chip
                      icon={<LocationOn sx={{ fontSize: 18 }} />}
                      label={formatCityState(school.city, school.state)}
                      variant="outlined"
                    />
                  </Box>
                  <Box display="flex" gap={1}>
                    {school.website && (
                      <Button
                        variant="contained"
                        startIcon={<Public />}
                        href={school.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Website
                      </Button>
                    )}
                    <Button
                      variant={isBookmarked ? 'contained' : 'outlined'}
                      color={isBookmarked ? 'warning' : 'primary'}
                      startIcon={isBookmarked ? <Star /> : <StarBorder />}
                      onClick={handleToggleBookmark}
                      disabled={addBookmark.isPending || removeBookmark.isPending}
                    >
                      {isBookmarked ? 'Saved' : 'Save School'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recruiters at this school */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recruiters at this school
              </Typography>
              {recruiters && recruiters.length > 0 ? (
                <List>
                  {recruiters.map(recruiter => (
                    <ListItem key={recruiter.id} alignItems="flex-start">
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText
                        primary={recruiter.position}
                        secondary={
                          <>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <Box display="flex" alignItems="center">
                                <Email sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography variant="body2">
                                  {recruiter.contactInfo.email}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center">
                                <Phone sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography variant="body2">
                                  {recruiter.contactInfo.phone}
                                </Typography>
                              </Box>
                              {recruiter.bio && (
                                <Typography variant="body2" color="text.secondary">
                                  {recruiter.bio}
                                </Typography>
                              )}
                            </Box>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recruiters are listed for this school yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Overview / Location */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overview
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText primary="Location" secondary={`${school.city}, ${school.state}`} />
                </ListItem>
                {school.conference && (
                  <ListItem>
                    <ListItemIcon>
                      <SchoolIcon />
                    </ListItemIcon>
                    <ListItemText primary="Conference" secondary={school.conference} />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
